import { Box, Typography } from "@outschool/backpack";
import {
  ActivityEnrollQueryQuery,
  EnrollmentPaymentFragmentFragment,
  GetLastUnrefundedEnrollmentPaymentQuery,
  GetLastUnrefundedEnrollmentPaymentQueryVariables
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import {
  ApolloError,
  gql,
  useQueryWithPreviousData
} from "@outschool/ui-apollo";
import React from "react";
import ReactSelect from "react-select";

import * as helpers from "../../../../shared/helpers";
import EnrollmentPaymentFragment from "../../../queries/EnrollmentPaymentFragment";
import { useOrderContext } from "../OrderContext";

type PrepayWeeklyPaymentsInputs = {
  endMeetingLabel?: string;
  setPurchaseOrderCoverage?: React.Dispatch<React.SetStateAction<Date | null>>;
  section: NonNullable<ActivityEnrollQueryQuery["enrollmentForm"]>["section"];
  selectedLearners?: string[];
  isOngoing: boolean;
};

export const PrepayWeeklyPaymentsInputs: React.FunctionComponent<
  PrepayWeeklyPaymentsInputs
> = ({
  endMeetingLabel = "Last week of class",
  setPurchaseOrderCoverage,
  section,
  selectedLearners,
  isOngoing
}: PrepayWeeklyPaymentsInputs) => {
  return (
    <>
      <EndMeetingInput
        label={endMeetingLabel}
        setPurchaseOrderCoverage={setPurchaseOrderCoverage}
        section={section}
        selectedLearners={selectedLearners}
        isOngoing={isOngoing}
      />
    </>
  );
};

const lastUnrefundedEnrollmentPaymentQuery = gql`
  query getLastUnrefundedEnrollmentPayment($sectionUid: ID!, $learnerUid: ID!) {
    lastUnrefundedEnrollmentPayment(
      sectionUid: $sectionUid
      learnerUid: $learnerUid
    ) {
      ...EnrollmentPaymentFragment
    }
  }
  ${EnrollmentPaymentFragment}
`;

interface UseLastUnrefundedEnrollmentPaymentQueryReturn {
  lastPayment: EnrollmentPaymentFragmentFragment | null | undefined;
  loading: boolean;
  refetch: (variables?: any) => void;
  error: ApolloError | undefined;
}

export function useLastUnrefundedEnrollmentPaymentQuery({
  sectionUid,
  learnerUid = ""
}: {
  sectionUid: string;
  learnerUid?: string;
}): UseLastUnrefundedEnrollmentPaymentQueryReturn {
  const { data, loading, error, refetch } = useQueryWithPreviousData<
    GetLastUnrefundedEnrollmentPaymentQuery,
    GetLastUnrefundedEnrollmentPaymentQueryVariables
  >(lastUnrefundedEnrollmentPaymentQuery, {
    variables: { sectionUid, learnerUid },
    fetchPolicy: "cache-and-network",
    skip: !learnerUid || learnerUid === ""
  });

  return {
    lastPayment: data?.lastUnrefundedEnrollmentPayment,
    loading,
    refetch,
    error
  };
}

type EndMeetingInputProps = {
  label: string;
  setPurchaseOrderCoverage?: React.Dispatch<React.SetStateAction<Date | null>>;
  section: NonNullable<ActivityEnrollQueryQuery["enrollmentForm"]>["section"];
  selectedLearners?: string[];
  isOngoing: boolean;
};

const EndMeetingInput: React.FunctionComponent<EndMeetingInputProps> = ({
  label,
  setPurchaseOrderCoverage,
  section,
  selectedLearners,
  isOngoing
}: EndMeetingInputProps) => {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\PrepayWeeklyPaymentsInputs"
  );
  const {
    setPrepayDurationWeeks,
    setIsWeeklyPayment,
    setOngoingStartTime,
    ongoingEndTime,
    setOngoingEndTime,
    prepaidOngoingPeriodOptions
  } = useOrderContext();

  const { endOfPeriodOptions } = prepaidOngoingPeriodOptions || {};

  const { lastPayment, loading } = useLastUnrefundedEnrollmentPaymentQuery({
    sectionUid: section?.uid,
    learnerUid:
      !!selectedLearners &&
      selectedLearners.length === 1 &&
      helpers.isUuid(selectedLearners?.[0])
        ? selectedLearners?.[0]
        : undefined
  });

  const placeholderOption: MeetingDaySelectOption = {
    label: t("Pay Upfront"),
    value: null
  };
  let options = [];
  if (!isOngoing) {
    options.push(placeholderOption);
  }

  const calcDurationWeeks = (
    date: Date | null,
    calculateFrom?: Date | null
  ) => {
    const startTime = calculateFrom ? dayjs(calculateFrom) : dayjs();
    return !!date ? dayjs(date).diff(dayjs(startTime), "weeks") + 1 : 0;
  };

  const handleOptionChange = (option: MeetingDaySelectOption) => {
    if (!!setOngoingStartTime) {
      setOngoingStartTime(
        option.value ? lastPaymentPeriodCollectedEndDate : null
      );
    }
    if (!!setOngoingEndTime) {
      setOngoingEndTime(option?.value);
    }
    setPrepayDurationWeeks(
      calcDurationWeeks(option?.value, lastPaymentPeriodCollectedEndDate)
    );
    setIsWeeklyPayment(option.value !== null);
    if (!!setPurchaseOrderCoverage) {
      setPurchaseOrderCoverage(option.value ? new Date(option.value) : null);
    }
  };

  let lastPaymentPeriodCollectedEndDate: Date;

  if (!loading) {
    lastPaymentPeriodCollectedEndDate = lastPayment?.end_time;

    const purchasableOptions = endOfPeriodOptions?.filter(
      (option: MeetingDaySelectOption) =>
        (!!option.value && option.value > lastPaymentPeriodCollectedEndDate) ||
        !lastPaymentPeriodCollectedEndDate
    );
    const minimumNumberOfWeeksToPurchase = isOngoing ? 1 : 4;
    const optionsWithMinimumDuration = purchasableOptions?.filter(
      (option: MeetingDaySelectOption) => {
        return (
          !!option.value &&
          calcDurationWeeks(option.value, purchasableOptions?.[0].value) >=
            minimumNumberOfWeeksToPurchase
        );
      }
    );
    const availableOptions = optionsWithMinimumDuration?.length
      ? optionsWithMinimumDuration
      : purchasableOptions;

    options = options.concat(availableOptions || []);

    if (isOngoing && ongoingEndTime == null) {
      handleOptionChange(options[0]);
    }
  }

  return (
    <MeetingDayInput
      defaultValue={options[0]}
      label={label}
      options={options}
      handleChange={handleOptionChange}
      value={
        options?.find(
          (option: MeetingDaySelectOption) => option.value === ongoingEndTime
        ) || options[0]
      }
    />
  );
};

/* MeetingDayInput */

export type MeetingDaySelectOption = {
  label: string;
  value: Date | null;
};

type MeetingDayInputProps = {
  label: string;
  options: MeetingDaySelectOption[];
  handleChange: (option: MeetingDaySelectOption) => void;
  defaultValue?: MeetingDaySelectOption;
  value: MeetingDaySelectOption | null;
};

const MeetingDayInput: React.FunctionComponent<MeetingDayInputProps> = ({
  label,
  options,
  handleChange,
  defaultValue,
  value
}) => (
  <Box
    sx={{
      marginTop: "1em"
    }}
  >
    <Typography
      variant="h6"
      sx={{
        my: "0.5em"
      }}
    >
      {label}
    </Typography>
    <ReactSelect
      options={options}
      onChange={(option: MeetingDaySelectOption) => handleChange(option)}
      defaultValue={defaultValue}
      value={value}
    />
  </Box>
);
